import {
  GenericCombobox,
  InputGroup,
  ALL_COUNTRIES,
  NON_EU_COUNTRIES_WITHOUT_POSTCODES,
  PageSection
} from '@/common'
import { ImporterFormData } from './ImporterForm.schema'
import { orderBy } from 'es-toolkit'
import { useCallback, useMemo } from 'react'
import useI18n from '@/shared/hooks/usei18n'

type ImporterFormProps = {
  data: ImporterFormData
  onChange: (data: ImporterFormData) => void
  fieldErrors?: {
    customId?: string
    identificationNumber?: string
  }
  resetErrorField: (field: 'customId' | 'identificationNumber') => void
}

export default function ImporterForm({
  data,
  onChange,
  fieldErrors,
  resetErrorField
}: ImporterFormProps) {
  const { t } = useI18n()
  const {
    name,
    customId,
    placeOfRegistration,
    identificationNumber,
    street,
    streetNumber,
    street2,
    postCode,
    city,
    subDivision,
    country,
    poBox
  } = data

  const importerPlaceOfRegistrationOptions = useMemo(() => {
    const allCountries = orderBy(
      ALL_COUNTRIES.map(value => ({
        label: t(`common:countriesByIsoAlpha2:${value}`),
        value
      })),
      [option => option.label.toLowerCase().normalize('NFKD')],
      ['asc']
    )

    // Manually add Switzerland to the list of place of registration options
    return [
      ...allCountries,
      {
        label: t('common:countriesByIsoAlpha2.ch'),
        value: 'ch'
      }
    ]
  }, [t])

  const onIdentificationNumberChange = useCallback(
    (value: string) => {
      onChange({ ...data, identificationNumber: value })
      if (fieldErrors?.identificationNumber) {
        resetErrorField('identificationNumber')
      }
    },
    [data, onChange, fieldErrors, resetErrorField]
  )

  const onCustomIdChange = useCallback(
    (value: string) => {
      onChange({ ...data, customId: value })
      if (fieldErrors?.customId) {
        resetErrorField('customId')
      }
    },
    [data, onChange, fieldErrors, resetErrorField]
  )

  return (
    <div className="space-y-6 pb-2">
      <PageSection title={t('importers:importerInputFields.sections.importersDetails')}>
        <div className="grid sm:grid-cols-2 gap-4">
          <InputGroup
            label={t('importers:importerInputFields.importerName')}
            required
            value={name || ''}
            onChange={evt => onChange({ ...data, name: evt.target.value })}
          />
          <GenericCombobox
            required
            label={t('importers:importerInputFields.placeOfRegistration')}
            value={
              placeOfRegistration
                ? {
                    value: placeOfRegistration,
                    label: t(`countriesByIsoAlpha2.${placeOfRegistration}`)
                  }
                : null
            }
            onChange={evt => {
              onChange({
                ...data,
                placeOfRegistration: evt?.value || '',
                country: evt?.value || ''
              })
            }}
            items={importerPlaceOfRegistrationOptions}
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <InputGroup
            id="identificationNumber"
            label={t('importers:importerInputFields.identificationNumber')}
            required
            value={identificationNumber || ''}
            error={fieldErrors?.identificationNumber}
            onChange={evt => onIdentificationNumberChange(evt.target.value)}
          />
          <InputGroup
            id="customId"
            label={t('importers:importerInputFields.customId')}
            value={customId || ''}
            error={fieldErrors?.customId}
            onChange={evt => onCustomIdChange(evt.target.value)}
          />
        </div>
      </PageSection>
      <PageSection title={t('importers:importerInputFields.sections.address')}>
        <div className="grid grid-cols-2 gap-4">
          <InputGroup
            id="street"
            label={t('common:inputFields.street.label')}
            value={street}
            onChange={evt => onChange({ ...data, street: evt.target.value })}
          />

          <InputGroup
            id="streetNumber"
            label={t('common:inputFields.streetNumber.label')}
            value={streetNumber}
            onChange={evt => onChange({ ...data, streetNumber: evt.target.value })}
          />
        </div>

        <InputGroup
          id="street2"
          label={t('common:inputFields.street2.label')}
          value={street2}
          onChange={evt => onChange({ ...data, street2: evt.target.value })}
        />

        <div className="grid md:grid-cols-3 gap-4">
          <InputGroup
            id="postCode"
            label={t('common:inputFields.postCode.label')}
            value={postCode}
            onChange={evt => onChange({ ...data, postCode: evt.target.value })}
            required={!NON_EU_COUNTRIES_WITHOUT_POSTCODES.includes(country)}
          />
          <InputGroup
            id="city"
            label={t('common:inputFields.city.label')}
            value={city}
            required
            onChange={evt => onChange({ ...data, city: evt.target.value })}
          />
          <InputGroup
            id="poBox"
            label={t('common:inputFields.poBox.label')}
            value={poBox}
            onChange={evt => onChange({ ...data, poBox: evt.target.value })}
          />
        </div>
        <InputGroup
          id="subDivision"
          label={t('common:inputFields.subDivision.label')}
          value={subDivision}
          onChange={evt => onChange({ ...data, subDivision: evt.target.value })}
        />
        <InputGroup
          label={t('common:inputFields.country.label')}
          value={country ? t(`common:countriesByIsoAlpha2:${country}`) : ''}
          disabled
        />
      </PageSection>
    </div>
  )
}
